import { Grid, Typography } from '@mui/material';
import { defaultStore as store } from '../../../Shared/Redux/defaultStore';
import * as dashboardActions from '../DashboardActions';

export function getYearFromUTC(utcValue: number): number {
    const date = new Date(utcValue);
    return date.getUTCFullYear();
}

export const config = {
    filters: [
        {
            id: 'savedTemplates',
            type: 'savedTemplates',
            label: 'Saved Templates',
        },
        // {
        //     id: 'lastModified',
        //     type: 'date',
        //     label: 'Date (Last Modified)',
        // },
        {
            id: 'status',
            type: 'list',
            label: 'Status',
            allowedOperator: ['eq', 'ne', 'in', 'nin'],
        },
        {
            id: 'cancerType',
            type: 'typeahead',
            label: 'Cancer type',
        },
        {
            id: 'cancerSubType',
            type: 'typeahead',
            label: 'Cancer subtype',
        },
        {
            id: 'icd10Code',
            type: 'typeahead',
            label: 'ICD10 code',
        },
        {
            id: 'clinicalStage',
            type: 'list',
            label: 'Clinical Stage',
        },
        {
            id: 'sex',
            type: 'list',
            label: 'Sex',
        },
        {
            id: 'age',
            type: 'number',
            label: 'Age',
        },
        {
            id: 'ageGroup',
            type: 'list',
            label: 'Age Group',
        },
        {
            id: 'treatmentLocation',
            type: 'typeahead',
            label: 'Treatment Location',
        },
        {
            id: 'physician',
            type: 'typeahead',
            label: 'Physician',
        },
        {
            id: 'routeToDiagnosis',
            type: 'typeahead',
            label: 'Route to Diagnosis',
        },
        {
            id: 'diagnosticTests',
            type: 'typeahead',
            label: 'Diagnostic Tests',
        },
        {
            id: 'geneMutations',
            type: 'typeahead',
            label: 'Gene mutations',
        },
        {
            id: 'otherConditions',
            type: 'typeahead',
            label: 'Other conditions',
        },
        {
            id: 'treatmentProcedure',
            type: 'typeahead',
            label: 'Treatment procedure',
        },
        {
            id: 'drugTreatment',
            type: 'typeahead',
            label: 'Drug treatment',
        },
        {
            id: 'responseToTreatment',
            type: 'typeahead',
            label: 'Response to Treatment',
        },
        {
            id: 'postTreatmentSurveillance',
            type: 'list',
            label: 'Post-treatment surveilance',
        },
        {
            id: 'registrationDate',
            type: 'date',
            label: 'Registration date',
        },
        {
            id: 'diagnosticsStartDate',
            type: 'date',
            label: 'Diagnostics start date',
        },
        {
            id: 'diagnosisDate',
            type: 'date',
            label: 'Diagnosis date',
        },
        {
            id: 'treatmentProcedureStartDate',
            type: 'date',
            label: 'Treatment procedure start date',
        },
        {
            id: 'drugTreatmentStartDate',
            type: 'date',
            label: 'Drug treatment start date',
        },
        {
            id: 'dateOfDeath',
            type: 'date',
            label: 'Date of death',
        },
        {
            id: 'isTrial',
            type: 'list',
            label: 'Trial Participation',
        },
    ],
    tabs: [
        {
            label: 'Trends',
            description:
                'Stats of the Pathologists, manners and causes of death registered in the system',
            builder: [
                {
                    type: 'customComponent',
                    width: 3,
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const registrationCount =
                            dashboard?.totalMortalityByYearCount?.items.filter(
                                (item) => item.registrationDate
                            ).length;

                        const mortalityCount =
                            dashboard?.totalMortalityByYearCount?.items.filter(
                                (item) => item.dateOfDeath
                            ).length;

                        return { registrationCount, mortalityCount };
                    },
                    customComponent: ({
                        registrationCount,
                        mortalityCount,
                    }) => {
                        return (
                            <>
                                <Grid
                                    container
                                    marginTop={2}
                                    spacing={10}
                                    direction="column"
                                    alignItems="center"
                                >
                                    <Grid item xs={12}>
                                        <Typography variant="h6" align="center">
                                            Total Case Count
                                        </Typography>
                                        <Typography
                                            variant="h2"
                                            align="center"
                                            color="primary"
                                        >
                                            {registrationCount}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="h6" align="center">
                                            Total Death Count
                                        </Typography>
                                        <Typography
                                            variant="h2"
                                            align="center"
                                            color="primary"
                                        >
                                            {mortalityCount}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </>
                        );
                    },
                },
                {
                    type: 'lineChart',
                    width: 9,
                    chartTitle: 'Cases and Deaths by Year',
                    colourScheme: ['#325599', '#FF6B81'],
                    tickAmount: 20,
                    showXLabels: true,
                    enablePoints: true,
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const combinationCounts: any = {};

                        dashboard?.totalMortalityByYearCount?.items?.forEach(
                            (item: any) => {
                                const {
                                    _id: { registrationDate, dateOfDeath },
                                } = item;

                                const registrationYear =
                                    getYearFromUTC(registrationDate);
                                const deathYear = getYearFromUTC(dateOfDeath);

                                if (deathYear) {
                                    const deathKey = `deathKey_${deathYear}`;
                                    combinationCounts[deathKey] =
                                        (combinationCounts[deathKey] || 0) + 1;
                                }

                                if (!deathYear) {
                                    const nonDeathKey = `nonDeathKey_${registrationYear}`;
                                    combinationCounts[nonDeathKey] =
                                        (combinationCounts[nonDeathKey] || 0) +
                                        1;
                                }
                            }
                        );

                        const years = Array.from(
                            new Set(
                                Object.keys(combinationCounts).map(
                                    (key) => key.split('_')[1]
                                )
                            )
                        ).sort((a, b) => Number(a) - Number(b));

                        chart.categories = years;

                        const result = {
                            items: [
                                {
                                    id: 'Cases',
                                    data: years.map((year) => {
                                        const key = `nonDeathKey_${year}`;
                                        return {
                                            x: year,
                                            y: combinationCounts[key] || 0,
                                        };
                                    }),
                                },
                                {
                                    id: 'Deaths',
                                    data: years.map((year) => {
                                        const key = `deathKey_${year}`;
                                        return {
                                            x: year,
                                            y: combinationCounts[key] || 0,
                                        };
                                    }),
                                },
                            ],
                        };

                        return result;
                    },
                    downloadableImage: true,
                    legendAnchor: 'top-right',
                    downloadableCsv: true,
                    enableGridX: true,
                    enableGridY: true,
                    showMarkers: true,
                    strokeWidth: 1,
                },
                {
                    type: 'barChart',
                    width: 6,
                    layout: 'horizontal',
                    chartTitle: 'Cases and Deaths by Cancer Type',
                    withBrush: 'true',
                    colourScheme: ['#325599', '#FF6B81'],
                    tickAmount: 5,
                    xAxisRotate: false,
                    showXLabels: true,
                    groupBy: 'cancerType',
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const counts: any = {};
                        const combinationCounts: any = {};

                        dashboard?.incidenceDeathsAndCancerType?.items?.forEach(
                            (item: any) => {
                                const {
                                    _id: { status, cancerType },
                                } = item;

                                if (counts[cancerType]) {
                                    counts[cancerType] += 1;
                                } else {
                                    counts[cancerType] = 1;
                                }

                                const nonDeathCount =
                                    status !== 'Death' ? 1 : 0;
                                const deathCount = status === 'Death' ? 1 : 0;

                                const nonDeathKey = `nonDeathKey_${cancerType}`;
                                const deathKey = `deathKey_${cancerType}`;

                                combinationCounts[nonDeathKey] =
                                    (combinationCounts[nonDeathKey] || 0) +
                                    nonDeathCount;
                                combinationCounts[deathKey] =
                                    (combinationCounts[deathKey] || 0) +
                                    deathCount;
                            }
                        );

                        const resultData = Object.keys(counts).map(
                            (cancerType) => ({
                                label: cancerType,
                                groupedBy: cancerType,
                                'Cases':
                                    combinationCounts[
                                        `nonDeathKey_${cancerType}`
                                    ] || 0,
                                Deaths:
                                    combinationCounts[
                                        `deathKey_${cancerType}`
                                    ] || 0,
                                total:
                                    (combinationCounts[
                                        `nonDeathKey_${cancerType}`
                                    ] || 0) +
                                    (combinationCounts[
                                        `deathKey_${cancerType}`
                                    ] || 0),
                            })
                        );

                        resultData.sort((b, a) => a.total - b.total);

                        const result = {
                            data: resultData.map(({ total, ...rest }) => rest),
                            keys: ['Deaths', 'Cases'],
                        };
                        return result;
                    },
                    dataType: 'incidenceDeathsAndCancerType',
                    legendAnchor: 'bottom-right',
                    downloadableImage: true,
                    downloadableCsv: true,
                    enableGridX: true,
                    groupMode: 'stacked',
                },
                {
                    type: 'pieChart',
                    width: 6,
                    chartType: 'donut',
                    chartTitle: 'Cases by Cancer Type',
                    filterBy: 'cancerType',
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const counts: any = {};

                        dashboard?.cancerType?.items?.forEach((item: any) => {
                            const {
                                _id: { cancerType },
                            } = item;

                            if (counts[cancerType]) {
                                counts[cancerType] += 1;
                            } else {
                                counts[cancerType] = 1;
                            }
                        });

                        const returnedObject = Object.keys(counts).map(
                            (label) => ({
                                label,
                                value: counts[label],
                            })
                        );

                        return returnedObject;
                    },
                    dataType: 'cancerType',
                    showPercent: true,
                    downloadableImage: true,
                    downloadableCsv: true,
                    legend: true,
                    legendAnchor: 'top-left',
                    enableRadialLabels: false,
                },
                {
                    type: 'pieChart',
                    width: 6,
                    chartType: 'donut',
                    chartTitle: 'Cases by Sex',
                    filterBy: 'sex',
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const counts: any = {};

                        dashboard?.sexOfIncidences?.items?.forEach(
                            (item: any) => {
                                const {
                                    _id: { sex },
                                } = item;

                                if (counts[sex]) {
                                    counts[sex] += 1;
                                } else {
                                    counts[sex] = 1;
                                }
                            }
                        );

                        const returnedObject = Object.keys(counts).map(
                            (label) => ({
                                label,
                                value: counts[label],
                            })
                        );

                        return returnedObject;
                    },
                    dataType: 'sexOfIncidences',
                    showPercent: true,
                    downloadableImage: true,
                    downloadableCsv: true,
                    legend: true,
                    legendAnchor: 'top-left',
                    enableRadialLabels: false,
                },
                {
                    type: 'pieChart',
                    width: 6,
                    chartType: 'donut',
                    chartTitle: 'Cases by Clinical Stage',
                    filterBy: 'clinicalStage',
                    customLegend: true,
                    toolTipCase: true,
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const counts: any = {};
                        dashboard?.clinicalStageOfIncidences?.items?.forEach(
                            (item: any) => {
                                const {
                                    _id: { clinicalStage },
                                } = item;

                                const normalizedClinicalStage = clinicalStage?.toUpperCase();

                                if (counts[normalizedClinicalStage]) {
                                    counts[normalizedClinicalStage] += 1;
                                } else {
                                    counts[normalizedClinicalStage] = 1;
                                }
                            }
                        );

                        const returnedObject = Object.keys(counts).map(
                            (label) => ({
                                label,
                                value: counts[label],
                            })
                        );

                        return returnedObject;
                    },
                    dataType: 'clinicalStageOfIncidences',
                    showPercent: true,
                    downloadableImage: true,
                    downloadableCsv: true,
                    legend: true,
                    legendAnchor: 'top-left',
                    enableRadialLabels: false,
                },
                {
                    type: 'barChart',
                    width: 6,
                    layout: 'vertical',
                    chartTitle: 'Cases by Age Group and Sex',
                    withBrush: 'true',
                    colourScheme: ['#325599', '#FF6B81'],
                    tickAmount: 10,
                    xLegend: 'Age Group',
                    xAxisRotate: true,
                    showXLabels: true,
                    aggregatedDataFilter: true,
                    customFilterAggregation: (data: any, item: string) => {

                        const input = data;
                        const result = input.replace(/\s+/g, '');

                        const filter = {
                            ageGroup: {
                                id: 'ageGroup',
                                type: 'STRING',
                                operator: 'in',
                                values: [result],
                            },
                        };

                        store.dispatch(dashboardActions.addFilter(filter));
                    },
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const counts: any = {};
                        const combinationCounts: any = {};

                        dashboard?.incidenceAgeAndSex?.items?.forEach(
                            (item: any) => {
                                const {
                                    _id: { age, sex },
                                } = item;

                                const getAgeBucket = (age_mean: number) => {
                                    if (age_mean < 0) return 'Unknown';
                                    if (age_mean >= 0 && age_mean <= 10)
                                        return '0 - 10';
                                    if (age_mean >= 11 && age_mean <= 20)
                                        return '11 - 20';
                                    if (age_mean >= 21 && age_mean <= 30)
                                        return '21 - 30';
                                    if (age_mean >= 31 && age_mean <= 40)
                                        return '31 - 40';
                                    if (age_mean >= 41 && age_mean <= 50)
                                        return '41 - 50';
                                    if (age_mean >= 51 && age_mean <= 60)
                                        return '51 - 60';
                                    if (age_mean >= 61 && age_mean <= 70)
                                        return '61 - 70';
                                    if (age_mean >= 71 && age_mean <= 80)
                                        return '71 - 80';
                                    if (age_mean >= 81 && age_mean <= 90)
                                        return '81 - 90';
                                    if (age_mean > 90) return '90+';
                                    return 'Unknown';
                                };

                                const ageBucket = getAgeBucket(age);

                                if (counts[ageBucket]) {
                                    counts[ageBucket] += 1;
                                } else {
                                    counts[ageBucket] = 1;
                                }

                                const maleCount = sex === 'Male' ? 1 : 0;
                                const femaleCount = sex === 'Female' ? 1 : 0;

                                const maleKey = `male_${ageBucket}`;
                                const femaleKey = `female_${ageBucket}`;

                                combinationCounts[maleKey] =
                                    (combinationCounts[maleKey] || 0) +
                                    maleCount;
                                combinationCounts[femaleKey] =
                                    (combinationCounts[femaleKey] || 0) +
                                    femaleCount;
                            }
                        );

                        const resultData = Object.keys(counts).map(
                            (ageBucket) => ({
                                label: ageBucket,
                                groupedBy: ageBucket,
                                male:
                                    combinationCounts[`male_${ageBucket}`] || 0,
                                female:
                                    combinationCounts[`female_${ageBucket}`] ||
                                    0,
                                total:
                                    (combinationCounts[`male_${ageBucket}`] ||
                                        0) +
                                    (combinationCounts[`female_${ageBucket}`] ||
                                        0),
                            })
                        );

                        const ageOrder = {
                            '0 - 10': 0,
                            '11 - 20': 1,
                            '21 - 30': 2,
                            '31 - 40': 3,
                            '41 - 50': 4,
                            '51 - 60': 5,
                            '61 - 70': 6,
                            '71 - 80': 7,
                            '81 - 90': 8,
                            '90+': 9,
                            Unknown: 10,
                        };

                        resultData.sort((a, b) => {
                            return ageOrder[a.label] - ageOrder[b.label];
                        });

                        const result = {
                            data: resultData.map(({ total, ...rest }) => rest),
                            keys: ['male', 'female'],
                        };
                        return result;
                    },
                    dataType: 'incidenceAgeAndSex',
                    legendAnchor: 'bottom-right',
                    downloadableImage: true,
                    downloadableCsv: true,
                    enableGridX: true,
                    groupMode: 'grouped',
                },
            ],
            queries: {
                totalIncidenceCount: {
                    addCount: true,
                    limit: 0,
                    fields: [],
                },
                totalMortalityCount: {
                    addCount: true,
                    limit: 0,
                    fields: ['_id'],
                    initialFilter: {
                        status: {
                            type: 'STRING',
                            eq: 'Death',
                        },
                    },
                },
                cancerType: {
                    addCount: true,
                    limit: 0,
                    groupBy: ['_id', 'cancerType'],
                },
                sexOfIncidences: {
                    addCount: true,
                    limit: 0,
                    groupBy: ['_id', 'sex'],
                },
                clinicalStageOfIncidences: {
                    addCount: true,
                    limit: 0,
                    groupBy: ['_id', 'clinicalStage'],
                },
                incidenceDeathsAndCancerType: {
                    addCount: true,
                    limit: 0,
                    groupBy: ['_id', 'cancerType', 'status'],
                },
                incidenceAgeAndSex: {
                    addCount: true,
                    limit: 0,
                    groupBy: ['_id', 'sex', 'age'],
                },
                totalMortalityByYearCount: {
                    addCount: true,
                    limit: 0,
                    groupBy: ['_id', 'registrationDate', 'dateOfDeath'],
                },
            },
        },
        {
            label: 'Diagnostics',
            description: 'Insights on incidences under diagnostic review.',
            builder: [
                {
                    type: 'customComponent',
                    width: 3,
                    countTitle: 'Total Cases Under Diagnostic Count',
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const underDiagnosticsCount = dashboard?.totalDiagnosticsCount?.items[0]?.COUNT
                        return { underDiagnosticsCount };
                    },
                    customComponent: ({
                        underDiagnosticsCount,
                    }) => {
                        return (
                            <>
                                <Grid
                                    container
                                    marginTop={2}
                                    spacing={10}
                                    direction="column"
                                    alignItems="center"
                                >
                                    <Grid item xs={12}>
                                        <Typography variant="h6" align="center" marginTop={4}>
                                            Total Cases Under Diagnostics Count
                                        </Typography>
                                        <Typography
                                            marginTop={2}
                                            variant="h2"
                                            align="center"
                                            color="primary"
                                        >
                                            {underDiagnosticsCount}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </>
                        );
                    },
                },
                {
                    type: 'areaChart',
                    width: 9,
                    chartTitle: 'Cases Under Diagnostics by Age Group and Sex',
                    tickAmount: 20,
                    showXLabels: true,
                    enablePoints: true,
                    xLegend: 'Age',
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const combinationCounts: any = {};

                        const getAgeBucket = (age_mean: number) => {
                            if (age_mean < 0) return 'Unknown';
                            if (age_mean >= 0 && age_mean <= 10)
                                return '0 - 10';
                            if (age_mean >= 11 && age_mean <= 20)
                                return '11 - 20';
                            if (age_mean >= 21 && age_mean <= 30)
                                return '21 - 30';
                            if (age_mean >= 31 && age_mean <= 40)
                                return '31 - 40';
                            if (age_mean >= 41 && age_mean <= 50)
                                return '41 - 50';
                            if (age_mean >= 51 && age_mean <= 60)
                                return '51 - 60';
                            if (age_mean >= 61 && age_mean <= 70)
                                return '61 - 70';
                            if (age_mean >= 71 && age_mean <= 80)
                                return '71 - 80';
                            if (age_mean >= 81 && age_mean <= 90)
                                return '81 - 90';
                            if (age_mean > 90) return '90+';
                            return 'Unknown';
                        };

                        dashboard?.diagnositicsCountOfAgeGroupAndSex?.items?.forEach(
                            (item: any) => {
                                const {
                                    _id: { age, sex },
                                } = item;

                                const ageBucket = getAgeBucket(age);

                                if (sex === 'Male') {
                                    const maleKey = `male_${ageBucket}`;
                                    combinationCounts[maleKey] =
                                        (combinationCounts[maleKey] || 0) + 1;
                                }

                                if (sex === 'Female') {
                                    const femaleKey = `female_${ageBucket}`;
                                    combinationCounts[femaleKey] =
                                        (combinationCounts[femaleKey] || 0) + 1;
                                }
                            }
                        );

                        const ages = Array.from(
                            new Set(
                                Object.keys(combinationCounts).map(
                                    (key) => key.split('_')[1]
                                )
                            )
                        );

                        const ageOrder = {
                            '0 - 10': 0,
                            '11 - 20': 1,
                            '21 - 30': 2,
                            '31 - 40': 3,
                            '41 - 50': 4,
                            '51 - 60': 5,
                            '61 - 70': 6,
                            '71 - 80': 7,
                            '81 - 90': 8,
                            '90+': 9,
                            Unknown: 10,
                        };

                        ages.sort((a, b) => ageOrder[a] - ageOrder[b]);

                        chart.categories = ages;

                        const result = {
                            data: [
                                {
                                    id: 'Female',
                                    data: ages.map((age) => {
                                        const key = `female_${age}`;
                                        return {
                                            x: age,
                                            y: combinationCounts[key] || 0,
                                        };
                                    }),
                                },
                                {
                                    id: 'Male',
                                    data: ages.map((age) => {
                                        const key = `male_${age}`;
                                        return {
                                            x: age,
                                            y: combinationCounts[key] || 0,
                                        };
                                    }),
                                },
                            ],
                        };

                        return result;
                    },
                    groupBy: 'id',
                    categories: [],
                    downloadableImage: true,
                    legendAnchor: 'top-right',
                    downloadableCsv: true,
                    enableGridX: true,
                    enableGridY: true,
                    showMarkers: true,
                    strokeWidth: 1,
                },
                {
                    type: 'barChart',
                    width: 6,
                    layout: 'horizontal',
                    chartTitle: 'Diagnostic Tests',
                    withBrush: 'true',
                    colourScheme: ['#325599', '#FF6B81'],
                    tickAmount: 5,
                    xAxisRotate: false,
                    groupBy: 'diagnosticTests',
                    showXLabels: true,
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const counts: any = {};

                        dashboard?.diagnositicsTestCount?.items?.forEach(
                            (item: any) => {
                                const {
                                    _id: { diagnosticTests },
                                } = item;

                                if (counts[diagnosticTests]) {
                                    counts[diagnosticTests] += 1;
                                } else {
                                    counts[diagnosticTests] = 1;
                                }
                            }
                        );

                        const resultData = Object.keys(counts).map(
                            (diagnosticTests) => ({
                                label: diagnosticTests,
                                groupedBy: diagnosticTests,
                                completed: counts[diagnosticTests],
                            })
                        );

                        resultData.sort((b, a) => a.completed - b.completed);

                        const result = {
                            data: resultData,
                            keys: ['completed'],
                        };
                        return result;
                    },
                    dataType: 'reportCounts',
                    legendAnchor: 'bottom-right',
                    downloadableImage: true,
                    downloadableCsv: true,
                    enableGridX: true,
                    groupMode: 'stacked',
                },
                {
                    type: 'pieChart',
                    width: 6,
                    chartType: 'donut',
                    chartTitle: 'Route to Diagnosis',
                    customLegend: true,
                    filterBy: 'routeToDiagnosis',
                    toolTipCase: true,
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const counts =
                            dashboard?.routeToDiagnosisCount?.items?.reduce(
                                (
                                    acc: Record<string, number>,
                                    { _id: { routeToDiagnosis } }: any
                                ) => {
                                    let normalized = routeToDiagnosis
                                        ?.toLowerCase()
                                        .replace('gp referral', 'GP Referral')
                                        .replace('screening', 'Screening');

                                    acc[normalized] =
                                        (acc[normalized] || 0) + 1;
                                    return acc;
                                },
                                {}
                            ) || {};

                        return Object.entries(counts).map(([label, value]) => ({
                            label,
                            value,
                        }));
                    },
                    dataType: 'routeToDiagnosisCount',
                    showPercent: true,
                    downloadableImage: true,
                    downloadableCsv: true,
                    legend: true,
                    legendAnchor: 'top-left',
                    enableRadialLabels: false,
                },
                {
                    type: 'pieChart',
                    width: 6,
                    chartType: 'donut',
                    chartTitle: 'Cases Under Diagnostics by Physician',
                    filterBy: 'physician',
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const counts: any = {};

                        dashboard?.physiciansOfDiagnositicsCount?.items?.forEach(
                            (item: any) => {
                                const {
                                    _id: { physician },
                                } = item;

                                if (counts[physician]) {
                                    counts[physician] += 1;
                                } else {
                                    counts[physician] = 1;
                                }
                            }
                        );

                        const returnedObject = Object.keys(counts).map(
                            (label) => ({
                                label,
                                value: counts[label],
                            })
                        );

                        return returnedObject;
                    },
                    dataType: 'physiciansOfDiagnositicsCount',
                    showPercent: true,
                    downloadableImage: true,
                    downloadableCsv: true,
                    legend: true,
                    legendAnchor: 'top-left',
                    enableRadialLabels: false,
                },
            ],
            queries: {
                totalDiagnosticsCount: {
                    addCount: true,
                    limit: 0,
                    fields: ['_id'],
                    initialFilter: {
                        status: {
                            type: 'STRING',
                            eq: 'Diagnostics',
                        },
                    },
                },
                routeToDiagnosisCount: {
                    addCount: true,
                    limit: 0,
                    groupBy: ['_id', 'routeToDiagnosis'],
                    initialFilter: {
                        status: {
                            type: 'STRING',
                            eq: 'Diagnostics',
                        },
                    },
                },
                diagnositicsCountOfAgeGroupAndSex: {
                    addCount: true,
                    limit: 0,
                    groupBy: ['_id', 'age', 'sex'],
                    initialFilter: {
                        status: {
                            type: 'STRING',
                            eq: 'Diagnostics',
                        },
                    },
                },
                diagnositicsTestCount: {
                    addCount: true,
                    limit: 0,
                    groupBy: ['_id', 'diagnosticTests'],
                    initialFilter: {
                        status: {
                            type: 'STRING',
                            eq: 'Diagnostics',
                        },
                    },
                },
                physiciansOfDiagnositicsCount: {
                    addCount: true,
                    limit: 0,
                    groupBy: ['_id', 'physician'],
                    initialFilter: {
                        status: {
                            type: 'STRING',
                            eq: 'Diagnostics',
                        },
                    },
                },
            },
        },
        {
            label: 'Treatment',
            description: 'Insights on incidences under treatment.',
            builder: [
                {
                    type: 'customComponent',
                    width: 3,
                    countTitle: 'Total Cases Under Treatment Count',
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const underTreatmentCount = dashboard?.totalTreatmentCount?.items[0]?.COUNT
                        return { underTreatmentCount };
                    },
                    customComponent: ({
                        underTreatmentCount,
                    }) => {
                        return (
                            <>
                                <Grid
                                    container
                                    marginTop={2}
                                    spacing={10}
                                    direction="column"
                                    alignItems="center"
                                >
                                    <Grid item xs={12}>
                                        <Typography variant="h6" align="center" marginTop={4}>
                                            Total Cases Under Treatment Count
                                        </Typography>
                                        <Typography
                                            marginTop={2}
                                            variant="h2"
                                            align="center"
                                            color="primary"
                                        >
                                            {underTreatmentCount}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </>
                        );
                    },
                },
                {
                    type: 'areaChart',
                    width: 9,
                    chartTitle: 'Cases Under Treatment by Age Group and Sex',
                    tickAmount: 20,
                    showXLabels: true,
                    enablePoints: true,
                    xLegend: 'Age',
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const combinationCounts: any = {};

                        const getAgeBucket = (age_mean: number) => {
                            if (age_mean < 0) return 'Unknown';
                            if (age_mean >= 0 && age_mean <= 10)
                                return '0 - 10';
                            if (age_mean >= 11 && age_mean <= 20)
                                return '11 - 20';
                            if (age_mean >= 21 && age_mean <= 30)
                                return '21 - 30';
                            if (age_mean >= 31 && age_mean <= 40)
                                return '31 - 40';
                            if (age_mean >= 41 && age_mean <= 50)
                                return '41 - 50';
                            if (age_mean >= 51 && age_mean <= 60)
                                return '51 - 60';
                            if (age_mean >= 61 && age_mean <= 70)
                                return '61 - 70';
                            if (age_mean >= 71 && age_mean <= 80)
                                return '71 - 80';
                            if (age_mean >= 81 && age_mean <= 90)
                                return '81 - 90';
                            if (age_mean > 90) return '90+';
                            return 'Unknown';
                        };

                        dashboard?.treatmentCountOfAgeGroupAndSex?.items?.forEach(
                            (item: any) => {
                                const {
                                    _id: { age, sex },
                                } = item;

                                const ageBucket = getAgeBucket(age);

                                if (sex === 'Male') {
                                    const maleKey = `male_${ageBucket}`;
                                    combinationCounts[maleKey] =
                                        (combinationCounts[maleKey] || 0) + 1;
                                }

                                if (sex === 'Female') {
                                    const femaleKey = `female_${ageBucket}`;
                                    combinationCounts[femaleKey] =
                                        (combinationCounts[femaleKey] || 0) + 1;
                                }
                            }
                        );

                        const ages = Array.from(
                            new Set(
                                Object.keys(combinationCounts).map(
                                    (key) => key.split('_')[1]
                                )
                            )
                        );

                        const ageOrder = {
                            '0 - 10': 0,
                            '11 - 20': 1,
                            '21 - 30': 2,
                            '31 - 40': 3,
                            '41 - 50': 4,
                            '51 - 60': 5,
                            '61 - 70': 6,
                            '71 - 80': 7,
                            '81 - 90': 8,
                            '90+': 9,
                            Unknown: 10,
                        };

                        ages.sort((a, b) => ageOrder[a] - ageOrder[b]);

                        chart.categories = ages;

                        const result = {
                            data: [
                                {
                                    id: 'Female',
                                    data: ages.map((age) => {
                                        const key = `female_${age}`;
                                        return {
                                            x: age,
                                            y: combinationCounts[key] || 0,
                                        };
                                    }),
                                },
                                {
                                    id: 'Male',
                                    data: ages.map((age) => {
                                        const key = `male_${age}`;
                                        return {
                                            x: age,
                                            y: combinationCounts[key] || 0,
                                        };
                                    }),
                                },
                            ],
                        };

                        return result;
                    },
                    categories: [],
                    downloadableImage: true,
                    legendAnchor: 'top-right',
                    downloadableCsv: true,
                    enableGridX: true,
                    enableGridY: true,
                    showMarkers: true,
                    strokeWidth: 1,
                },
                {
                    type: 'barChart',
                    width: 6,
                    layout: 'horizontal',
                    chartTitle: 'Cancer Type by Sex',
                    withBrush: 'true',
                    colourScheme: ['#325599', '#FF6B81'],
                    tickAmount: 5,
                    xAxisRotate: false,
                    showXLabels: true,
                    groupBy: 'cancerType',
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const counts: any = {};
                        const combinationCounts: any = {};

                        dashboard?.treatmentsByCancerTypeAndSex?.items?.forEach(
                            (item: any) => {
                                const {
                                    _id: { cancerType, sex },
                                } = item;

                                if (counts[cancerType]) {
                                    counts[cancerType] += 1;
                                } else {
                                    counts[cancerType] = 1;
                                }

                                const maleCount = sex === 'Male' ? 1 : 0;
                                const femaleCount = sex === 'Female' ? 1 : 0;

                                const maleKey = `maleKey_${cancerType}`;
                                const femaleKey = `femaleKey_${cancerType}`;

                                combinationCounts[maleKey] =
                                    (combinationCounts[maleKey] || 0) +
                                    maleCount;
                                combinationCounts[femaleKey] =
                                    (combinationCounts[femaleKey] || 0) +
                                    femaleCount;
                            }
                        );

                        const resultData = Object.keys(counts).map(
                            (cancerType) => ({
                                label: cancerType,
                                groupedBy: cancerType,
                                Male:
                                    combinationCounts[
                                        `maleKey_${cancerType}`
                                    ] || 0,
                                Female:
                                    combinationCounts[
                                        `femaleKey_${cancerType}`
                                    ] || 0,
                                total:
                                    (combinationCounts[
                                        `maleKey_${cancerType}`
                                    ] || 0) +
                                    (combinationCounts[
                                        `femaleKey_${cancerType}`
                                    ] || 0),
                            })
                        );

                        resultData.sort((b, a) => a.total - b.total);

                        const result = {
                            data: resultData.map(({ total, ...rest }) => rest),
                            keys: ['Male', 'Female'],
                        };
                        return result;
                    },
                    dataType: 'treatmentsByCancerTypeAndSex',
                    legendAnchor: 'bottom-right',
                    downloadableImage: true,
                    downloadableCsv: true,
                    enableGridX: true,
                    groupMode: 'stacked',
                },
                {
                    type: 'pieChart',
                    width: 6,
                    chartType: 'donut',
                    chartTitle: 'Treatment Procedure',
                    filterBy: 'treatmentProcedure',
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const counts: any = {};

                        let treatment;

                        dashboard?.treatmentsByProcedure?.items?.forEach(
                            (item: any) => {
                                const {
                                    _id: { treatmentProcedure },
                                } = item;

                                treatmentProcedure === undefined
                                    ? (treatment = 'No Data Provided')
                                    : (treatment = treatmentProcedure);

                                if (counts[treatment]) {
                                    counts[treatment] += 1;
                                } else {
                                    counts[treatment] = 1;
                                }
                            }
                        );

                        const returnedObject = Object.keys(counts).map(
                            (label) => ({
                                label,
                                value: counts[label],
                            })
                        );

                        return returnedObject;
                    },
                    dataType: 'treatmentsByProcedure',
                    showPercent: true,
                    downloadableImage: true,
                    downloadableCsv: true,
                    legend: true,
                    legendAnchor: 'top-left',
                    enableRadialLabels: false,
                },
                {
                    type: 'barChart',
                    width: 6,
                    layout: 'horizontal',
                    chartTitle: 'Drug Treatment',
                    withBrush: 'true',
                    colourScheme: ['#325599', '#FF6B81'],
                    tickAmount: 10,
                    xAxisRotate: false,
                    showXLabels: true,
                    groupBy: 'drugTreatment',
                    customFilterAggregation: (data: any, item: string) => {

                        const filter = {
                            drugTreatment: {
                                id: 'drugTreatment',
                                type: 'STRING',
                                operator: 'in',
                                values: [data],
                            },
                        };

                        store.dispatch(dashboardActions.addFilter(filter));
                    },
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const counts: any = {};

                        dashboard?.treatmentsByDrugType?.items?.forEach(
                            (item: any) => {
                                const {
                                    _id: { drugTreatment },
                                } = item;

                                if (drugTreatment === undefined) return;

                                if (counts[drugTreatment]) {
                                    counts[drugTreatment] += 1;
                                } else {
                                    counts[drugTreatment] = 1;
                                }
                            }
                        );

                        const resultData = Object.keys(counts).map(
                            (drugTreatment) => ({
                                label: drugTreatment,
                                groupedBy: drugTreatment,
                                completed: counts[drugTreatment],
                            })
                        );

                        resultData.sort((b, a) => a.completed - b.completed);

                        const result = {
                            data: resultData,
                            keys: ['completed'],
                        };
                        return result;
                    },
                    dataType: 'treatmentsByDrugType',
                    legendAnchor: 'bottom-right',
                    downloadableImage: true,
                    downloadableCsv: true,
                    enableGridX: true,
                    groupMode: 'stacked',
                },
                {
                    type: 'pieChart',
                    width: 6,
                    chartType: 'donut',
                    chartTitle: 'Trial Participation',
                    filterBy: 'isTrial',
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const counts: any = {};

                        dashboard?.trialParticipationCount?.items?.forEach(
                            (item: any) => {
                                const {
                                    _id: { isTrial },
                                } = item;

                                if (counts[isTrial]) {
                                    counts[isTrial] += 1;
                                } else {
                                    counts[isTrial] = 1;
                                }
                            }
                        );

                        const returnedObject = Object.keys(counts).map(
                            (label) => ({
                                label,
                                value: counts[label],
                            })
                        );

                        return returnedObject;
                    },
                    dataType: 'trialParticipationCount',
                    showPercent: true,
                    downloadableImage: true,
                    downloadableCsv: true,
                    legend: true,
                    legendAnchor: 'top-left',
                    enableRadialLabels: false,
                },
                {
                    type: 'pieChart',
                    width: 6,
                    chartType: 'donut',
                    chartTitle: 'Cases Under Treatment by Physician',
                    filterBy: 'physician',
                    dataTransformer: (
                        data: any,
                        chart: any,
                        dashboard: any
                    ) => {
                        const counts: any = {};

                        dashboard?.physiciansOfTreatmentCount?.items?.forEach(
                            (item: any) => {
                                const {
                                    _id: { physician },
                                } = item;

                                if (counts[physician]) {
                                    counts[physician] += 1;
                                } else {
                                    counts[physician] = 1;
                                }
                            }
                        );

                        const returnedObject = Object.keys(counts).map(
                            (label) => ({
                                label,
                                value: counts[label],
                            })
                        );

                        return returnedObject;
                    },
                    dataType: 'physiciansOfTreatmentCount',
                    showPercent: true,
                    downloadableImage: true,
                    downloadableCsv: true,
                    legend: true,
                    legendAnchor: 'top-left',
                    enableRadialLabels: false,
                },
            ],
            queries: {
                totalTreatmentCount: {
                    addCount: true,
                    limit: 0,
                    fields: ['_id'],
                    initialFilter: {
                        status: {
                            type: 'STRING',
                            eq: 'Treatment',
                        },
                    },
                },
                trialParticipationCount: {
                    addCount: true,
                    limit: 0,
                    groupBy: ['_id', 'isTrial'],
                    initialFilter: {
                        status: {
                            type: 'STRING',
                            eq: 'Treatment',
                        },
                    },
                },
                physiciansOfTreatmentCount: {
                    addCount: true,
                    limit: 0,
                    groupBy: ['_id', 'physician'],
                    initialFilter: {
                        status: {
                            type: 'STRING',
                            eq: 'Treatment',
                        },
                    },
                },
                treatmentsByDrugType: {
                    addCount: true,
                    limit: 0,
                    groupBy: ['_id', 'drugTreatment'],
                    initialFilter: {
                        status: {
                            type: 'STRING',
                            eq: 'Treatment',
                        },
                    },
                },
                treatmentsByProcedure: {
                    addCount: true,
                    limit: 0,
                    groupBy: ['_id', 'status', 'treatmentProcedure'],
                    initialFilter: {
                        status: {
                            type: 'STRING',
                            eq: 'Treatment',
                        },
                    },
                },
                treatmentsByCancerTypeAndSex: {
                    addCount: true,
                    limit: 0,
                    groupBy: ['_id', 'cancerType', 'status', 'sex'],
                    initialFilter: {
                        status: {
                            type: 'STRING',
                            eq: 'Treatment',
                        },
                    },
                },
                treatmentCountOfAgeGroupAndSex: {
                    addCount: true,
                    limit: 0,
                    groupBy: ['_id', 'age', 'sex'],
                    initialFilter: {
                        status: {
                            type: 'STRING',
                            eq: 'Treatment',
                        },
                    },
                },
            },
        },
        {
            label: 'Case List',
            description: 'List of the registered entries in the system',
            builder: [
                {
                    type: 'tableComponent',
                    dataTransformer: (data: { items: any }) => {
                        return {
                            data: data && data.items,
                            columns: [
                                {
                                    name: '_id',
                                    label: 'Case ID',
                                    options: {
                                        customBodyRender: (value: string) => {
                                            if (value) {
                                                if (value.includes('/')) {
                                                    return value
                                                        .split('/')
                                                        .pop();
                                                } else {
                                                    return value;
                                                }
                                            } else {
                                                return '---';
                                            }
                                        },
                                    },
                                },
                                {
                                    name: 'status',
                                    label: 'Status',
                                    options: {
                                        customBodyRender: (value: string) => {
                                            if (value) {
                                                return value;
                                            } else {
                                                return '---';
                                            }
                                        },
                                    },
                                },
                                {
                                    name: 'age',
                                    label: 'Age',
                                    options: {
                                        customBodyRender: (value: string) => {
                                            if (value) {
                                                return value;
                                            } else {
                                                return '---';
                                            }
                                        },
                                    },
                                },
                                {
                                    name: 'cancerType',
                                    label: 'Cancer Type',
                                    options: {
                                        customBodyRender: (value: string) => {
                                            if (value) {
                                                return value;
                                            } else {
                                                return '---';
                                            }
                                        },
                                    },
                                },
                                {
                                    name: 'icd10Code',
                                    label: 'ICD10 Code',
                                    options: {
                                        customBodyRender: (value: string) => {
                                            if (value) {
                                                return value;
                                            } else {
                                                return '---';
                                            }
                                        },
                                    },
                                },
                                {
                                    name: 'clinicalStage',
                                    label: 'Clinical Stage',
                                    options: {
                                        customBodyRender: (value: string) => {
                                            if (value) {
                                                return value;
                                            } else {
                                                return '---';
                                            }
                                        },
                                    },
                                },
                                {
                                    name: 'treatmentProcedure',
                                    label: 'Treatment Procedure',
                                    options: {
                                        customBodyRender: (value: string) => {
                                            if (value) {
                                                return value;
                                            } else {
                                                return '---';
                                            }
                                        },
                                    },
                                },
                                {
                                    name: 'drugTreatment',
                                    label: 'Drug Treatment',
                                    options: {
                                        customBodyRender: (value: string) => {
                                            if (value) {
                                                return value;
                                            } else {
                                                return '---';
                                            }
                                        },
                                    },
                                },
                                {
                                    name: 'responseToTreatment',
                                    label: 'Response to Treatment',
                                    options: {
                                        customBodyRender: (value: string) => {
                                            if (value) {
                                                return value;
                                            } else {
                                                return '---';
                                            }
                                        },
                                    },
                                },
                                {
                                    name: 'trialId',
                                    label: 'Trial ID',
                                    options: {
                                        customBodyRender: (value: string) => {
                                            if (value) {
                                                return value;
                                            } else {
                                                return '---';
                                            }
                                        },
                                    },
                                },
                            ],
                        };
                    },
                    downloadableCsv: true,
                    serverSide: true,
                    width: '12',
                    idField: '_id',
                    dataType: 'tableData',
                    rowClick: true,
                    hideExport: true,
                    showIdField: true,
                    sort: true,
                },
                {
                    type: 'query',
                    dataTransformer: 'simpleCount',
                    countTitle: 'Total Report Count',
                    chartTitle: 'Report Total',
                    width: '3',
                    dataType: 'reportCounts',
                },
            ],
            queries: {
                reportCounts: {
                    addCount: true,
                    limit: 0,
                    fields: [],
                    groupBy: [],
                    sortAsc: false,
                    sortField: 'COUNT',
                    where: 'getWhere(null, filter)',
                },
                tableData: {
                    from: 0,
                    limit: 10,
                    fields: [
                        '_id',
                        'status',
                        'age',
                        'cancerType',
                        'icd10Code',
                        'clinicalStage',
                        'treatmentProcedure',
                        'drugTreatment',
                        'responseToTreatment',
                        'trialId',
                    ],
                    sortAsc: false,
                    sortField: '_id',
                    initialFilter: {},
                },
            },
            dependentQueries: {
                rename_query: {
                    addCount: true,
                    limit: 0,
                    fields: [],
                    groupBy: [],
                    sortAsc: false,
                    sortField: 'COUNT',
                    initialFilter: {},
                },
            },
        },
    ],
};
